import React, { memo, useEffect, useState } from "react";
import Dropdown from "../generic/Dropdown";
import ButtonGroupOrganizer from "../generic/ButtonGroupOrganizer";

// Constants for Unit Systems
const UNIT_SYSTEMS = {
    METRIC: "Metric" as const,
    IMPERIAL: "Imperial" as const,
};

// Define constants for Metric and Imperial Units
const METRIC_UNITS = ["meters", "centimeters", "millimeters", "kilograms", "pieces", "sets"];
const IMPERIAL_UNITS = ["feet", "inches", "yards", "pounds", "pieces", "sets"];

const QuantityUnitDropdown: React.FC<{
    value: string | null;
    onChange: (value: string | null) => void;
}> = memo(({ value, onChange }) => {
    const initialUnitSystem =
        !value || (METRIC_UNITS.includes(value) && IMPERIAL_UNITS.includes(value))
            ? UNIT_SYSTEMS.IMPERIAL
            : METRIC_UNITS.includes(value)
                ? UNIT_SYSTEMS.METRIC
                : UNIT_SYSTEMS.IMPERIAL;

    // Fix: Use the correct types for unitSystem: "Metric" | "Imperial"
    const [unitSystem, setUnitSystem] = useState<"Metric" | "Imperial">(initialUnitSystem);
    const [availableUnits, setAvailableUnits] = useState<string[]>(
        initialUnitSystem === UNIT_SYSTEMS.METRIC ? METRIC_UNITS : IMPERIAL_UNITS,
    );

    useEffect(() => {
        const newUnits = unitSystem === UNIT_SYSTEMS.METRIC ? METRIC_UNITS : IMPERIAL_UNITS;
        setAvailableUnits(newUnits);

        if (value && !newUnits.includes(value)) {
            onChange(null);
        }
    }, [unitSystem, value]);

    return (
        <div className="quantity-unit-dropdown">
            <div className="input mb-4">
                <label className={"pb-3"}>Unit System</label>
                <div className="quantity-unit-dropdown-buttons flex space-x-2">
                    <ButtonGroupOrganizer>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="radio"
                                name="unitSystem"
                                value={UNIT_SYSTEMS.IMPERIAL}
                                checked={unitSystem === UNIT_SYSTEMS.IMPERIAL}
                                onChange={() => setUnitSystem(UNIT_SYSTEMS.IMPERIAL)}
                                className="hidden"
                            />
                            <span
                                className={`w-32 px-4 py-2 rounded transition-transform duration-300 ease-in-out ring-0 ${unitSystem === UNIT_SYSTEMS.IMPERIAL ? "font-bold border-blue-500 dark:border-blue-400 text-blue-500 dark:text-blue-400 bg-blue-100 dark:bg-blue-900 shadow-inner" : "opacity-80 text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 shadow-sm"} hover:opacity-100 hover:scale-105`}>
                                {UNIT_SYSTEMS.IMPERIAL}
                            </span>
                        </label>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="radio"
                                name="unitSystem"
                                value={UNIT_SYSTEMS.METRIC}
                                checked={unitSystem === UNIT_SYSTEMS.METRIC}
                                onChange={() => setUnitSystem(UNIT_SYSTEMS.METRIC)}
                                className="hidden"
                            />
                            <span
                                className={`w-32 px-4 py-2 rounded transition-transform duration-300 ease-in-out ring-0 ${unitSystem === UNIT_SYSTEMS.METRIC ? "font-bold border-blue-500 dark:border-blue-400 text-blue-500 dark:text-blue-400 bg-blue-100 dark:bg-blue-900 shadow-inner" : "opacity-80 text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 shadow-sm"} hover:opacity-100 hover:scale-105`}>
                                {UNIT_SYSTEMS.METRIC}
                            </span>
                        </label>
                    </ButtonGroupOrganizer>
                </div>
            </div>
            <Dropdown
                label="Quantity Unit"
                options={availableUnits.map((unit) => ({
                    value: unit,
                    label: unit.charAt(0).toUpperCase() + unit.slice(1),
                }))}
                selectedValue={value}
                onChange={onChange}
                placeholder="Select a unit"
                required={true}
            />
        </div>
    );
});

export default QuantityUnitDropdown;
